package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.RhDepartmentSuggestion
import br.com.vvrefeicoes.portal.domain.model.RhSheetKind
import br.com.vvrefeicoes.portal.domain.model.RhSheetLoad
import br.com.vvrefeicoes.portal.domain.model.RhUnitMatchStatus
import br.com.vvrefeicoes.portal.domain.model.RhUnitOption
import br.com.vvrefeicoes.portal.domain.model.RhUnitPreview
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.RhSheetRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteRhSheetRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : RhSheetRepository {
    override suspend fun load(
        kind: RhSheetKind,
        period: MonthPeriod,
    ): RhSheetLoad? {
        val response =
            httpClient.get(baseUrl + "/rh/${kind.path}?year=${period.year}&month=${period.month}") { authorize() }
        if (response.status == HttpStatusCode.NotFound) return null
        ensureSuccess(response)
        val dto = response.body<RhSheetDto>()
        return RhSheetLoad(rows = dto.rows, updatedBy = dto.updatedBy, updatedAt = dto.updatedAt)
    }

    override suspend fun save(
        kind: RhSheetKind,
        period: MonthPeriod,
        rows: List<Map<String, String>>,
        unitByDepartment: Map<String, String?>,
    ) {
        val response =
            httpClient.put(baseUrl + "/rh/${kind.path}") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(
                    RhSheetRequestDto(
                        ano = period.year,
                        mes = period.month,
                        rows = rows,
                        mappings = unitByDepartment.map { (department, unitId) -> RhUnitMappingDto(department, unitId) },
                    ),
                )
            }
        ensureSuccess(response)
    }

    override suspend fun previewUnits(departments: List<String>): RhUnitPreview {
        val response =
            httpClient.post(baseUrl + "/rh/units/preview") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(RhUnitPreviewRequestDto(departments))
            }
        ensureSuccess(response)
        return response.body<RhUnitPreviewDto>().toDomain()
    }

    override suspend fun delete(
        kind: RhSheetKind,
        period: MonthPeriod,
    ) {
        val response =
            httpClient.delete(baseUrl + "/rh/${kind.path}?year=${period.year}&month=${period.month}") { authorize() }
        ensureSuccess(response)
    }

    private suspend fun ensureSuccess(response: HttpResponse) {
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<RhApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class RhSheetDto(
    val rows: List<Map<String, String>> = emptyList(),
    val updatedBy: String = "",
    val updatedAt: String = "",
)

@Serializable
private data class RhSheetRequestDto(
    val ano: Int,
    val mes: Int,
    val rows: List<Map<String, String>>,
    val mappings: List<RhUnitMappingDto>,
)

@Serializable
private data class RhApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class RhUnitMappingDto(
    val department: String,
    val unitId: String?,
)

@Serializable
private data class RhUnitPreviewRequestDto(
    val departments: List<String>,
)

@Serializable
private data class RhUnitPreviewDto(
    val units: List<RhUnitOptionDto> = emptyList(),
    val departments: List<RhDepartmentSuggestionDto> = emptyList(),
) {
    fun toDomain() =
        RhUnitPreview(
            units = units.map { RhUnitOption(it.id, it.name, it.director, it.manager) },
            departments = departments.map { RhDepartmentSuggestion(it.department, it.suggestedUnitId, it.status.toMatchStatus()) },
        )
}

@Serializable
private data class RhUnitOptionDto(
    val id: String,
    val name: String,
    val director: String? = null,
    val manager: String? = null,
)

@Serializable
private data class RhDepartmentSuggestionDto(
    val department: String,
    val suggestedUnitId: String? = null,
    val status: String = "NOT_FOUND",
)

/** O backend detalha o critério da sugestão; para a conferência só importa o quanto ela é confiável. */
private fun String.toMatchStatus(): RhUnitMatchStatus =
    when (this) {
        "ALIAS" -> RhUnitMatchStatus.CONFIRMED
        "EXACT" -> RhUnitMatchStatus.EXACT
        "NOT_FOUND" -> RhUnitMatchStatus.NOT_FOUND
        else -> RhUnitMatchStatus.SUGGESTED
    }
